import { Component, OnInit } from "@angular/core";
import { WhatsNewService } from "../../shared/services/whats-new/whats-new.service";

type WhatsNewEntry = {
  id: number;
  heading: string;
  body_text: string;
  tag?: string | null;
  link_url?: string | null;
  link_text?: string | null;
  button_text?: string | null;
  publish_date: number | null;
  created_at?: number | null;
  updated_at?: number | null;
  is_unread?: boolean;
};

type WhatsNewDateGroup = {
  publish_date: number | null;
  unread?: boolean;
  entries: WhatsNewEntry[];
};

@Component({
  selector: "app-whats-new",
  templateUrl: "./whats-new.component.html",
  styleUrls: ["./whats-new.component.scss"],
})
export class WhatsNewComponent implements OnInit {
  dates: WhatsNewDateGroup[] = [];
  selectedDate: WhatsNewDateGroup | null = null;
  loading = false;
  hasUnread = false;

  constructor(private whatsNewService: WhatsNewService) {}

  async ngOnInit() {
    await this.loadWhatsNew();
  }

  async loadWhatsNew() {
    this.loading = true;
    (await this.whatsNewService.getWhatsNew()).subscribe((res: any) => {
      this.loading = false;
      const result =
        res?.result ||
        res?.data?.result ||
        res?.data ||
        res ||
        {};
      this.dates = (result?.dates || []).map((dateItem: any) => ({
        publish_date: this.toNumber(dateItem?.publish_date),
        unread: !!dateItem?.unread,
        entries: (dateItem?.entries || []).map((entry: any) => ({
          id: entry?.id,
          heading: entry?.heading || "",
          body_text: entry?.body_text || "",
          tag: entry?.tag || entry?.category || entry?.type || null,
          link_url: entry?.link_url || null,
          link_text: entry?.link_text || null,
          button_text: entry?.button_text || null,
          publish_date: this.toNumber(entry?.publish_date),
          created_at: this.toNumber(entry?.created_at),
          updated_at: this.toNumber(entry?.updated_at),
          is_unread: !!entry?.is_unread,
        })),
      }));
      this.hasUnread = !!result?.has_unread;
      this.whatsNewService.setHasUnread(this.hasUnread);
      this.selectedDate = this.dates?.length ? this.dates[0] : null;
    });
  }

  selectDate(date: WhatsNewDateGroup) {
    this.selectedDate = date;
    this.markDateRead(date);
  }

  async openEntry(entry: WhatsNewEntry) {
    // no-op: clicking content should not change styles or read state
  }

  tagClass(tag: string | null | undefined): string {
    const normalized = (tag || "").toLowerCase().trim();
    if (!normalized) return "";
    return `tag-${normalized.replace(/[^a-z0-9]+/g, "-")}`;
  }

  formatBody(text: string): string {
    if (!text) return "";
    const escaped = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");
    const withBold = escaped.replace(/(\*\*|__)(.+?)\1/g, "<strong>$2</strong>");
    const withItalic = withBold.replace(/(\*|_)([^*_]+?)\1/g, "<em>$2</em>");
    const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/g;
    return withItalic.replace(urlRegex, (raw) => {
      const href = raw.startsWith("http") ? raw : `https://${raw}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${raw}</a>`;
    }).replace(/\n/g, "<br/>");
  }

  private updateUnreadFlags() {
    this.dates = this.dates.map((date) => {
      const hasUnread = (date?.entries || []).some((e) => e.is_unread);
      return { ...date, unread: hasUnread };
    });
    this.hasUnread = this.dates.some((date) => date.unread);
    this.whatsNewService.setHasUnread(this.hasUnread);
  }

  private async markDateRead(date: WhatsNewDateGroup) {
    const unreadEntries = (date?.entries || []).filter((e) => e.is_unread);
    if (!unreadEntries.length) {
      return;
    }
    const entryIds = unreadEntries.map((entry) => entry.id).filter(Boolean);
    if (!entryIds.length) {
      return;
    }
    (await this.whatsNewService.markEntryRead(entryIds)).subscribe(() => {
      unreadEntries.forEach((entry) => {
        entry.is_unread = false;
      });
      this.updateUnreadFlags();
    });
  }

  private toNumber(value: any): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }
}
